import { useEffect } from 'react';
import { usePartyStore } from '../store/partyStore';
import type { SceneId, VisualStyle } from '../types';
import { SCENE_IDS, VISUAL_STYLES } from '../types';

const STYLE_IDS = VISUAL_STYLES.map((s) => s.id);

export function useKeyboardControls(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const store = usePartyStore.getState();

      switch (e.key) {
        case '+':
        case '=':
          store.adjustIntensity(0.1);
          break;
        case '-':
        case '_':
          store.adjustIntensity(-0.1);
          break;
        case 'l':
        case 'L':
          store.toggleLyrics();
          break;
        case 'k':
        case 'K':
          store.cycleLyricDisplayMode();
          break;
        case 'b':
        case 'B':
          store.toggleBlackout();
          break;
        case ']':
          store.adjustStrobeBoost(0.15);
          break;
        case '[':
          store.adjustStrobeBoost(-0.15);
          break;
        case 'n':
        case 'N':
          store.skipScene();
          break;
        case 'o':
        case 'O':
          store.toggleSceneLock();
          break;
        case 't':
        case 'T':
          store.registerTap();
          break;
        case 'f':
        case 'F':
          if (document.fullscreenElement) document.exitFullscreen();
          else document.documentElement.requestFullscreen().catch(() => {});
          break;
        case 'h':
        case 'H':
          store.toggleOverlay();
          break;
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
        case '0': {
          const idx = e.key === '0' ? 9 : Number(e.key) - 1;
          if (STYLE_IDS[idx]) store.setVisualStyle(STYLE_IDS[idx] as VisualStyle);
          break;
        }
        case 's':
        case 'S': {
          const current = store.overrides.forcedSceneId ?? store.activeEvent?.sceneId ?? 'neon-tunnel';
          const idx = SCENE_IDS.indexOf(current);
          const next = SCENE_IDS[(idx + 1) % SCENE_IDS.length] as SceneId;
          store.setForcedScene(next);
          if (!store.overrides.sceneLocked) store.toggleSceneLock();
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled]);
}
